import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { referralPortalClient } from "@/services/referralPortalClient";

import "./referralPortal.css";

export default function ReferralPortalLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [status, setStatus] = useState<string>("");
  const [statusTone, setStatusTone] = useState<"info" | "success" | "warning" | "danger">("info");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const session = referralPortalClient.getSession();

  async function handleRequestCode(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await referralPortalClient.requestLoginCode(email);
      setStep("code");
      setStatusTone("success");
      setStatus(
        `Code sent to ${result.email}. It expires at ${new Date(result.expiresAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}.`
      );
    } catch (error) {
      setStatusTone("danger");
      setStatus((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyCode(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await referralPortalClient.verifyLoginCode(email, code);
      navigate("/refer/dashboard");
    } catch (error) {
      setStatusTone("danger");
      setStatus((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="referral-portal">
      <div className="referral-portal__shell">
        <div className="referral-portal__layout">
          <section className="referral-portal__panel">
            <div className="referral-portal__eyebrow">Portal Login</div>
            <h1>Sign in with your portal email</h1>
            <p>
              We send an 8-digit code to existing client contacts already known in PMapp, plus
              approved referral admins. If your email is not recognized yet, use the join flow instead.
            </p>
            <div className="referral-portal__panel-actions">
              <button className="referral-portal__button" onClick={() => navigate("/refer/join")}>
                Request Access
              </button>
              {session ? (
                <button className="referral-portal__button--ghost" onClick={() => navigate("/refer/dashboard")}>
                  Continue as {session.email}
                </button>
              ) : null}
            </div>
          </section>

          <aside className="referral-portal__card">
            {step === "email" ? (
              <form className="referral-portal__form" onSubmit={handleRequestCode}>
                <div className="referral-portal__field">
                  <label htmlFor="portal-email">Email</label>
                  <input
                    id="portal-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@clientdomain.com.au"
                    required
                  />
                </div>
                <button className="referral-portal__button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send login code"}
                </button>
              </form>
            ) : (
              <form className="referral-portal__form" onSubmit={handleVerifyCode}>
                <div className="referral-portal__field">
                  <label htmlFor="portal-code">8-digit code</label>
                  <input
                    id="portal-code"
                    inputMode="numeric"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder="12345678"
                    maxLength={8}
                    required
                  />
                </div>
                <div className={`referral-portal__status referral-portal__status--${statusTone}`}>
                  {status}
                </div>
                <div className="referral-portal__actions">
                  <button className="referral-portal__button" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Verifying..." : "Verify and continue"}
                  </button>
                  <button className="referral-portal__button--ghost" type="button" onClick={() => setStep("email")}>
                    Change email
                  </button>
                </div>
              </form>
            )}

            {status && step === "email" ? (
              <div className={`referral-portal__status referral-portal__status--${statusTone}`}>{status}</div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
